import { useQuery } from '@tanstack/react-query';
import { useBatchJobs } from './useBatchJobs';
import { useDocumentCounts } from './useDocumentCounts';
import { getMetrics } from '../services/dashboard-api';
import type { SystemMetrics, PipelineStage, EntityType } from '../types';

/**
 * Hook to get system-wide metrics from API
 * This now fetches from /dashboard/metrics endpoint which includes entityBreakdown
 * @param entityType - Optional entity type filter ('person', 'company', 'opportunity', 'note', or undefined for all)
 */
export function useSystemMetrics(entityType?: EntityType): {
  metrics: SystemMetrics;
  isLoading: boolean;
} {
  const { data: metrics, isLoading } = useQuery({
    queryKey: ['systemMetrics', entityType],
    queryFn: () => getMetrics(entityType),
    refetchInterval: 15000, // Refresh every 15 seconds
    staleTime: 0, // Always fetch fresh data (no caching)
    cacheTime: 0, // Don't cache responses
  });

  // Return API metrics if available, otherwise return default values
  return {
    metrics: metrics || {
      activeBatches: 0,
      pendingDocuments: 0,
      documentsInActiveBatches: 0,
      totalIndexed: 0,
      indexedToday: 0,
      failedDocuments: 0,
      dlqDocuments: 0,
      successRate: 100,
      currentThroughput: 0,
      systemStatus: 'healthy',
      fetchedAt: new Date().toISOString(),
      queueDepths: {
        entityIngestVisible: 0,
        entityIngestNotVisible: 0,
        opensearchIndexVisible: 0,
        opensearchIndexNotVisible: 0,
        entityIngestDLQ: 0,
        opensearchIndexDLQ: 0,
      },
    },
    isLoading,
  };
}

/**
 * Hook to get pipeline stage counts
 * Uses metrics.documentsInActiveBatches for AI Processing to show DOCUMENT count, not batch count
 */
export function usePipelineStages(entityType?: 'person' | 'company' | 'opportunity' | 'note'): PipelineStage[] {
  const { data: documentCounts } = useDocumentCounts();
  const { metrics } = useSystemMetrics(entityType);

  if (!documentCounts) {
    return [];
  }

  return [
    {
      name: 'Queued',
      count: documentCounts.queued || 0,
      status: documentCounts.queued > 1000 ? 'warning' : 'healthy',
    },
    {
      name: 'Compiled',
      count: documentCounts.compiled || 0,
      status: documentCounts.compiled > 500 ? 'warning' : 'healthy',
    },
    {
      name: 'Batched',
      count: documentCounts.batched || 0,
      status: 'healthy',
    },
    {
      name: 'AI Processing',
      count: metrics.documentsInActiveBatches, // ← Now shows DOCUMENT count, not batch count
      status: 'healthy',
    },
    {
      name: 'Ready',
      count: documentCounts.ready_for_index || 0,
      status: documentCounts.ready_for_index > 200 ? 'warning' : 'healthy',
    },
    {
      name: 'Indexed',
      count: documentCounts.indexed || 0,
      status: 'healthy',
    },
  ];
}
